import apiClient, { type ApiError } from './apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { API_ENDPOINTS } from './config'
import type {
  GatewayStatusMetaMap,
  PaymentLogDetailResponse,
  PaymentLogDetailSource,
} from '@/types/paymentLog'

/** Resolve gateway label + description (Angular `paymentresponse` / payment log detail). */
export function gatewayMetaLookup(
  meta: GatewayStatusMetaMap | null,
  paymentSource: string | undefined,
  gatewayStatus: string | undefined,
): { display: string; description: string } {
  if (!gatewayStatus) return { display: '-', description: '' }
  if (!meta || !paymentSource) {
    return { display: gatewayStatus, description: '' }
  }
  const src = paymentSource
  const row =
    meta[src]?.[gatewayStatus] ?? meta[src.toUpperCase()]?.[gatewayStatus]
  return {
    display: row?.gateway_status_display ?? gatewayStatus,
    description: row?.gateway_status_desc ?? '',
  }
}

/**
 * Load payment log / adjustment detail (Angular `PaymentLogsDetailComponent`).
 * - `payment_log` → GET `/api/payment_log/:id`
 * - `payment_adjustment` → GET `/api/payment_adjustment/:paId`
 */
export async function getPaymentLogDetail(
  id: string | number,
  source: PaymentLogDetailSource = 'payment_log',
): Promise<PaymentLogDetailResponse> {
  try {
    const url =
      source === 'payment_adjustment'
        ? API_ENDPOINTS.PAYMENT_ADJUSTMENT_DETAIL(id)
        : API_ENDPOINTS.PAYMENT_LOG_DETAIL(id)
    const response = await apiClient.get<PaymentLogDetailResponse>(url)
    return response.data
  } catch (error) {
    const apiError = error as ApiError & { data?: unknown }
    throw {
      message: extractErrorMessage(
        error,
        'Failed to fetch payment log details.',
      ),
      status: apiError.status,
      // Preserve interceptor body so showApiError can render HTML/mailto (Angular notify).
      data: apiError.data,
    } as ApiError & { data?: unknown }
  }
}

/** Unwrap Angular store shape `{ paymentresponse: { CARDCONNECT: … } }` when present. */
export function normalizeGatewayStatusMetaMap(
  raw: unknown,
): GatewayStatusMetaMap {
  if (!raw || typeof raw !== 'object') return {}
  const o = raw as Record<string, unknown>
  const nested = o.paymentresponse
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as GatewayStatusMetaMap
  }
  return o as GatewayStatusMetaMap
}

/** Reference data for gateway status labels (Angular payment log detail). */
export async function getGatewayStatuses(): Promise<GatewayStatusMetaMap> {
  try {
    const response = await apiClient.get<unknown>(
      API_ENDPOINTS.GATEWAY_STATUSES,
    )
    return normalizeGatewayStatusMetaMap(response.data)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load gateway statuses.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function generateChargebackPacket(
  applicantId: string | number,
  paymentLogId?: string | number,
): Promise<void> {
  try {
    const body: Record<string, unknown> = { applicant_id: applicantId }
    if (paymentLogId != null && paymentLogId !== '') {
      body.payment_id = paymentLogId
    }
    await apiClient.post('/api/generate_chargebackpacket', body)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to generate chargeback packet.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}
